import type { ECharts, EChartsOption } from 'echarts';
import type { ChartExportOptions } from './types';

/**
 * Downloads a data URL as file
 * @param dataUrl - Data URL to download
 * @param filename - Name of the downloaded file
 * @example
 * downloadDataUrl('data:image/png;base64,...', 'chart.png')
 */
function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** Breathing room between the expanded legend and the plot above it, in px. */
const EXPORT_LEGEND_GAP = 12;

type ComponentOption = Record<string, unknown>;

type LayoutSnapshot = {
  legend?: ComponentOption[];
  dataZoom?: ComponentOption[];
  grid?: ComponentOption[];
};

/**
 * Height of the legend as it was actually laid out, or `undefined` when the
 * chart will not say.
 *
 * ECharts publishes no geometry — `getOption()` returns the authored values,
 * not computed ones — so this reaches into the rendered view. Every hop is
 * optional and the whole thing is wrapped, so a future ECharts that moves this
 * costs the export its grid reservation and nothing else.
 */
function measureLegendHeight(chartInstance: ECharts): number | undefined {
  try {
    const internals = chartInstance as unknown as {
      getModel?: () => { getComponent?: (mainType: string, index: number) => unknown } | undefined;
      getViewOfComponentModel?: (model: unknown) => { group?: { getBoundingRect?: () => { height: number } } };
    };

    const legendModel = internals.getModel?.()?.getComponent?.('legend', 0);

    if (!legendModel) {
      return undefined;
    }

    const height = internals.getViewOfComponentModel?.(legendModel)?.group?.getBoundingRect?.().height;

    return typeof height === 'number' && Number.isFinite(height) ? height : undefined;
  } catch {
    return undefined;
  }
}

/** Resolves an ECharts offset (`40`, `'18%'`) against the canvas height. */
function toPixels(value: unknown, canvasHeight: number): number | undefined {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string' && value.endsWith('%')) {
    const percent = Number.parseFloat(value);

    return Number.isFinite(percent) ? (percent / 100) * canvasHeight : undefined;
  }

  return undefined;
}

/**
 * Re-lays the chart out for a static snapshot, then restores it.
 *
 * Dashboard charts paginate their legend so it cannot wrap into the plot (see
 * `dashboard-charts/lib/chart-options.ts`), and ECharts draws a paginated
 * legend inside a clip path — a snapshot of the live instance would carry only
 * the current page and its "1/3" counter. An exported image has no pages, so
 * for the duration of the capture the legend becomes `plain` and takes as many
 * rows as it needs. Two things have to move out of its way:
 *
 * - the dataZoom slider, which sits in the band the legend grows into and is a
 *   control nobody can operate in a PNG. It is hidden rather than shifted:
 *   its handles and range labels overhang its configured `height`, so stacking
 *   it above the legend still collides.
 * - the plot itself, whose `grid.bottom` is reserved from the measured legend
 *   height so a tall legend cannot climb into the chart.
 *
 * `replaceMerge` rather than a plain merge: merging `scroll` → `plain` leaves
 * the scroll controller's layout behind and lands the legend ~15px off.
 *
 * Every step runs synchronously inside one task, so the browser never paints
 * the expanded state and the on-screen chart does not flicker.
 */
function withPrintableLayout<T>(chartInstance: ECharts, capture: () => T): T {
  const snapshot = chartInstance.getOption() as LayoutSnapshot | undefined;
  const legends = snapshot?.legend;

  if (!Array.isArray(legends) || !legends.some((legend) => legend?.type === 'scroll')) {
    return capture();
  }

  const dataZooms = Array.isArray(snapshot?.dataZoom) ? snapshot.dataZoom : [];
  const grids = Array.isArray(snapshot?.grid) ? snapshot.grid : [];
  const hasDataZooms = dataZooms.length > 0;

  chartInstance.setOption(
    {
      legend: legends.map((legend) => ({ ...legend, type: legend?.type === 'scroll' ? 'plain' : legend?.type })),
      ...(hasDataZooms
        ? {
            dataZoom: dataZooms.map((dataZoom) => ({
              ...dataZoom,
              ...(dataZoom?.type === 'slider' ? { show: false } : {})
            }))
          }
        : {})
    } as EChartsOption,
    { replaceMerge: hasDataZooms ? ['legend', 'dataZoom'] : ['legend'] }
  );

  const legendHeight = measureLegendHeight(chartInstance);
  const canvasHeight = chartInstance.getHeight();
  const reservedGrids =
    legendHeight === undefined || grids.length === 0
      ? undefined
      : grids.map((grid) => ({
          ...grid,
          // Legends are anchored to `bottom`, so the reservation starts there.
          bottom: Math.max(
            toPixels(grid?.bottom, canvasHeight) ?? 0,
            (toPixels(legends[0]?.bottom, canvasHeight) ?? 0) + legendHeight + EXPORT_LEGEND_GAP
          )
        }));

  if (reservedGrids) {
    chartInstance.setOption({ grid: reservedGrids } as EChartsOption, { replaceMerge: ['grid'] });
  }

  try {
    return capture();
  } finally {
    chartInstance.setOption(
      {
        legend: legends,
        ...(hasDataZooms ? { dataZoom: dataZooms } : {}),
        ...(reservedGrids ? { grid: grids } : {})
      } as EChartsOption,
      {
        replaceMerge: [
          'legend',
          ...(hasDataZooms ? (['dataZoom'] as const) : []),
          ...(reservedGrids ? (['grid'] as const) : [])
        ]
      }
    );
  }
}

/**
 * Exports ECharts instance to PNG image
 * @param chartInstance - ECharts instance
 * @param options - Export configuration
 * @example
 * const chartRef = useRef<ReactECharts>(null);
 * exportChartToPNG(
 *   chartRef.current?.getEchartsInstance(),
 *   { filename: 'dashboard-chart.png', pixelRatio: 2 }
 * )
 */
export function exportChartToPNG(chartInstance: ECharts | undefined | null, options?: ChartExportOptions): void {
  if (!chartInstance) {
    console.error('Chart instance is not available');
    return;
  }

  try {
    const dataUrl = withPrintableLayout(chartInstance, () =>
      chartInstance.getDataURL({
        type: 'png',
        pixelRatio: options?.pixelRatio || 2,
        backgroundColor: options?.backgroundColor || '#ffffff',
        excludeComponents: options?.excludeComponents
      })
    );

    downloadDataUrl(dataUrl, options?.filename || 'chart.png');
  } catch (error) {
    console.error('Failed to export chart to PNG:', error);
    throw new Error('Failed to export chart to PNG');
  }
}

/**
 * Exports ECharts instance to SVG format
 * @param chartInstance - ECharts instance
 * @param options - Export configuration
 * @example
 * exportChartToSVG(chartInstance, { filename: 'chart.svg' })
 */
export function exportChartToSVG(chartInstance: ECharts | undefined | null, options?: ChartExportOptions): void {
  if (!chartInstance) {
    console.error('Chart instance is not available');
    return;
  }

  try {
    const svg = withPrintableLayout(chartInstance, () => chartInstance.renderToSVGString());
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = options?.filename || 'chart.svg';
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to export chart to SVG:', error);
    throw new Error('Failed to export chart to SVG');
  }
}

/**
 * Extracts data from series with category-value objects
 * @example
 * extractCategoryValueData([
 *   { name: 'Series1', data: [{ category: 'Jan', value: 100 }] }
 * ])
 */
function extractCategoryValueData(series: Array<{ name?: string; data?: unknown }>): {
  headers: string[];
  rows: (string | number | null)[][];
} {
  const headers = ['Category'];
  const categoriesMap = new Map<string, (number | null)[]>();

  series.forEach((s, index) => {
    if (s.name) headers.push(s.name);

    const items = s.data as Array<{ category: string; value: number }> | undefined;
    items?.forEach((item) => {
      if (!categoriesMap.has(item.category)) {
        categoriesMap.set(item.category, new Array(series.length).fill(null));
      }
      categoriesMap.get(item.category)![index] = item.value;
    });
  });

  const rows = Array.from(categoriesMap.entries()).map(([category, values]) => [category, ...values]);

  return { headers, rows };
}

/**
 * Extracts data from pie chart series
 * @example
 * extractPieData([{ name: 'Pie', value: 100 }])
 */
function extractPieData(data: unknown[]): {
  headers: string[];
  rows: (string | number)[][];
} {
  const headers = ['Name', 'Value'];
  const rows = (data as Array<{ name?: string; value?: number }>)
    .filter((item) => item.name !== undefined && item.value !== undefined)
    .map((item) => [item.name!, item.value!]);

  return { headers, rows };
}

/**
 * Extracts data from standard line/bar charts
 * @example
 * extractStandardData(
 *   ['Mon', 'Tue'],
 *   [{ name: 'Sales', data: [100, 200] }]
 * )
 */
function extractStandardData(
  categories: string[],
  series: Array<{ name?: string; data?: unknown }>
): {
  headers: string[];
  rows: (string | number | null)[][];
} {
  const headers = ['Category'];
  series.forEach((s) => {
    if (s.name) headers.push(s.name);
  });

  const rows = categories.map((category, index) => {
    const row: (string | number | null)[] = [category];
    series.forEach((s) => {
      const values = s.data as number[] | undefined;
      row.push(values?.[index] ?? null);
    });
    return row;
  });

  return { headers, rows };
}

/**
 * Converts chart data to table format for export
 * @param chartOption - ECharts option object
 * @returns Headers and rows for table export
 * @example
 * extractChartData({
 *   xAxis: { data: ['Mon', 'Tue'] },
 *   series: [{ name: 'Sales', data: [100, 200] }]
 * })
 */
export function extractChartData(chartOption: EChartsOption | Record<string, unknown>): {
  headers: string[];
  rows: (string | number | boolean | null)[][];
} {
  const option = chartOption as Record<string, unknown>;
  const series = option.series as Array<{ name?: string; data?: unknown }> | undefined;

  if (!series?.length) {
    return { headers: [], rows: [] };
  }

  const firstData = series[0].data as unknown[] | undefined;
  if (!firstData?.length) {
    return { headers: [], rows: [] };
  }

  const firstItem = firstData[0];

  // Check data format
  if (typeof firstItem === 'object' && firstItem !== null) {
    if ('category' in firstItem && 'value' in firstItem) {
      return extractCategoryValueData(series);
    }
    if ('name' in firstItem && 'value' in firstItem) {
      return extractPieData(firstData);
    }
  }

  // Standard number array format
  const xAxis = option.xAxis as { data?: string[] } | undefined;
  if (xAxis?.data) {
    return extractStandardData(xAxis.data, series);
  }

  return { headers: [], rows: [] };
}
